import { TenantLogo } from "@/shared/ui/TenantLogo";
import Link from "@/shared/lib/router/link";
import { useRouter } from "@/shared/lib/router/router";
import { useContext, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Archive,
  BarChart3,
  Bell,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  MessagesSquare,
  Home,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Mail,
  Moon,
  Search,
  ScanText,
  Settings,
  Sun,
  Ticket,
  User as UserIcon,
  Users,
  X,
} from "lucide-react";
import { authContext } from "@/shared/model/authContext";
import { ROUTES } from "@/shared/config/routes";
import { PERMISSIONS } from "@/shared/config/permissions";
import { useSearchShortcut } from "./useSearchShortcut";
import { buildPlatformNavItem, buildProfileTabs, platformToolsInApp, type NavItem, type NavSubItem } from "./navItems";
import { PREDEFINITIONS_MANAGEMENT_PERMISSIONS } from "@/shared/lib/predefinitionsAccess";
import { useContractedModules } from "@/entities/session/model/contractedModules";
import { isAppBuildsEnabled } from "@/entities/app-build/lib/appBuildEnvironment";
import { useNotifications } from "@/features/notification-center/model/NotificationContext";
import { useSidebar as useSidebarPreference } from "@/shared/model/SidebarContext";
import { useProject } from "@/entities/project/model/projectContext";
import { useTheme } from "@/shared/model/ThemeContext";
import { useUserPreferences } from "@/entities/user/model/UserPreferencesContext";
import { useIsTauri } from "@/shared/lib/hooks/useIsTauri";
import { Button } from "@/shared/ui/kit/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/shared/ui/kit/sidebar";
import SearchModal from "./SearchModal";
import { TenantSwitcher } from "./TenantSwitcher";
import { NotificationPanel } from "@/features/notification-center/ui/NotificationPanel";
import ProjectSidebarMenu from "@/features/projects/components/sidebar/ProjectSidebarMenu";
import InventorySidebarMenu from "@/features/inventory/components/workspace/InventorySidebarMenu";
import { useUnreadTopics } from "@/entities/chat/model/useUnreadTopics";

function UtilityButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="relative text-white/80 hover:bg-white/10 hover:text-white dark:hover:bg-white/10"
    >
      {children}
    </Button>
  );
}

export default function SideBar() {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { isAutoCollapsed } = useSidebarPreference();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const expanded = isMobile || state === "expanded";
  const isTauri = useIsTauri();

  const { checkPermission, signOut, userProfile, avatarUrl, userRoles, isPlatformAdmin } = useContext(authContext);
  const { totalUnread } = useNotifications();
  const { projectPermissions } = useProject();
  const { isDark } = useTheme();
  const { updatePreference } = useUserPreferences();

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);

  // Persiste o tema no backend (fonte de verdade). Usar apenas o toggle do
  // ThemeContext gravava só no localStorage, e a hidratação do /me revertia o
  // tema no reload.
  const toggleTheme = () => {
    void updatePreference("theme", isDark ? "light" : "dark");
  };

  useSearchShortcut(setIsSearchModalOpen);

  useEffect(() => {
    setOpenMobile(false);
  }, [router.asPath, setOpenMobile]);

  const closeMobile = () => setOpenMobile(false);
  const tab = router.query.tab;
  const isContracted = useContractedModules();

  const isActive = (path: string | string[]) => {
    const paths = Array.isArray(path) ? path : [path];
    return paths.some((p) => (p === "/" ? router.pathname === "/" : router.pathname.startsWith(p)));
  };

  const hasAnyPermission = (permission: string) => checkPermission(permission) || projectPermissions.includes(permission);

  const hasFinancialPermission = isContracted("financial") && (hasAnyPermission(PERMISSIONS.CAN_LIST_COSTS) || hasAnyPermission(PERMISSIONS.CAN_LIST_OFFER_COSTS));
  const canListStorages = checkPermission(PERMISSIONS.CAN_LIST_STORAGES);
  const canListCategories = checkPermission(PERMISSIONS.CAN_LIST_CATEGORIES);
  const canListOffers = checkPermission(PERMISSIONS.CAN_LIST_OFFERS);
  const canListTools = checkPermission(PERMISSIONS.CAN_LIST_TOOLS);
  const canListStockTools = checkPermission(PERMISSIONS.CAN_LIST_STOCK_TOOLS);
  const canListStockBalances = checkPermission(PERMISSIONS.CAN_LIST_STOCK_BALANCES);
  const canListStocks = checkPermission(PERMISSIONS.CAN_LIST_STOCKS);
  const canListActionHistory = checkPermission(PERMISSIONS.CAN_LIST_ACTION_HISTORY);
  const canListSolicitations = checkPermission(PERMISSIONS.CAN_LIST_SOLICITATIONS);
  const canListUsers = checkPermission(PERMISSIONS.CAN_LIST_USERS);
  const canListSystemProfiles = checkPermission(PERMISSIONS.CAN_LIST_SYSTEM_PROFILES);
  const canListSuppliers = checkPermission(PERMISSIONS.CAN_LIST_SUPPLIERS);
  const canListCollaborators = checkPermission(PERMISSIONS.CAN_LIST_COLLABORATORS);
  const canListRoles = checkPermission(PERMISSIONS.CAN_LIST_ROLES);
  const canListPermission = checkPermission(PERMISSIONS.CAN_LIST_PERMISSION);
  // Gestão de predefinições: NÃO usar CAN_LIST_STEP_NAMES/CAN_LIST_TASK_NAMES aqui,
  // pois utilizadores operacionais têm essas permissões só para o autocomplete de
  // etapas/atividades. O menu administrativo exige permissão de gestão. Ver issue #1.1.
  const canManagePredefinitionsFlag = PREDEFINITIONS_MANAGEMENT_PERMISSIONS.some(checkPermission);
  const canListTasks = checkPermission(PERMISSIONS.CAN_LIST_TASKS);
  const canListContacts = checkPermission(PERMISSIONS.CAN_LIST_CONTACTS);
  const canListAppBuilds = platformToolsInApp(isPlatformAdmin) && isContracted("appHub") && isAppBuildsEnabled();
  const canViewErrors = platformToolsInApp(isPlatformAdmin);

  const isAdmin = userRoles?.some(r => r.toUpperCase().includes("ADMIN")) || canListSystemProfiles || canListUsers;
  const canAccessCRM = isContracted("crm") && (checkPermission(PERMISSIONS.CAN_ACCESS_CRM) || isAdmin);
  const unreadMessages = useUnreadTopics(canAccessCRM ? "all" : undefined);
  const canListProjects = checkPermission(PERMISSIONS.CAN_LIST_PROJECTS) || checkPermission(PERMISSIONS.CAN_LIST_PARTICIPATING_PROJECTS);
  const hasUsersModuleAccess = canListUsers || canListSystemProfiles || canListSuppliers || canListCollaborators;
  const hasSettingsModuleAccess = canListSystemProfiles || canListRoles || canListPermission || canManagePredefinitionsFlag;
  const canAccessInventoryModule = isContracted("inventory") && (canListStorages || canListCategories || canListOffers || canListTools
    || canListStockTools || canListStockBalances || canListStocks || canListActionHistory || canListSolicitations);
  const canReviewSubmits = checkPermission(PERMISSIONS.CAN_LIST_SUBMITS) || checkPermission(PERMISSIONS.CAN_APPROVE_SUBMIT);

  const inProject = router.pathname.startsWith("/project/");
  const inInventory = isActive([ROUTES.storages, "/inventory"]) || router.pathname.startsWith("/storage/");

  const items: NavItem[] = [
    { key: "home", href: ROUTES.home, icon: Home, label: t("sidebar.home"), active: isActive(ROUTES.home), visible: true },
    {
      key: "dashboard", href: ROUTES.dashboard, icon: LayoutDashboard, label: t("sidebar.dashboards"),
      active: isActive(ROUTES.dashboard), visible: isContracted("dashboards") && (hasFinancialPermission || canListProjects || canListTasks),
      subItems: [
        { href: `${ROUTES.dashboard}?tab=financial`, label: t("dashboard.financial"), active: tab === "financial" || !tab, visible: hasFinancialPermission },
        { href: `${ROUTES.dashboard}?tab=projects`, label: t("dashboard.projects"), active: tab === "projects", visible: canListProjects },
        { href: `${ROUTES.dashboard}?tab=tasks`, label: t("dashboard.tasksPanel"), active: tab === "tasks", visible: canListTasks },
      ],
    },
    {
      key: "users", href: ROUTES.users, icon: Users, label: t("sidebar.sub.records"),
      active: isActive(ROUTES.users), visible: hasUsersModuleAccess,
      subItems: [
        { href: `${ROUTES.users}?tab=users`, label: t("sidebar.sub.users"), active: tab === "users" || !tab, visible: canListUsers },
        { href: `${ROUTES.users}?tab=clients`, label: t("sidebar.sub.clients"), active: tab === "clients", visible: canListSystemProfiles },
        { href: `${ROUTES.users}?tab=suppliers`, label: t("sidebar.sub.suppliers"), active: tab === "suppliers", visible: canListSuppliers },
        { href: `${ROUTES.users}?tab=collaborators`, label: t("sidebar.sub.staff"), active: tab === "collaborators", visible: canListCollaborators },
      ],
    },
    {
      key: "projects", href: ROUTES.projects, icon: Briefcase, label: t("sidebar.projects"),
      active: isActive(ROUTES.projects), visible: canListProjects || inProject,
      expandChildren: isActive(ROUTES.projects) || inProject, submenu: <ProjectSidebarMenu />,
    },
    {
      key: "financial", href: ROUTES.financial, icon: BarChart3, label: t("sidebar.financial"),
      active: isActive(ROUTES.financial), visible: hasFinancialPermission,
      subItems: ["financeiro", "relatorio", "controle", "solicitacoes", "atendimentos"].map((key, index) => ({
        href: `${ROUTES.financial}?tab=${key}`,
        label: t(`financial.tabs.${key}`),
        active: tab === key || (index === 0 && !tab),
      })),
    },
    {
      key: "inventory", href: ROUTES.storages, icon: Archive, label: t("sidebar.inventory"),
      active: inInventory, visible: canAccessInventoryModule, submenu: <InventorySidebarMenu />,
    },
    { key: "documentExtractions", href: ROUTES.documentExtractions, icon: ScanText, label: t("sidebar.documentExtractions"), active: isActive(ROUTES.documentExtractions), visible: checkPermission(PERMISSIONS.CAN_EXTRACT_DOCUMENTS) },
    { key: "tickets", href: ROUTES.tickets, icon: Ticket, label: "Chamados", active: isActive(ROUTES.tickets), visible: isContracted("tickets") },
    { key: "crm", href: ROUTES.crmChat, icon: MessagesSquare, label: t("sidebar.sub.crmChat"), active: isActive(ROUTES.crmChat), visible: canAccessCRM, badge: unreadMessages },
    {
      key: "contacts", href: ROUTES.contacts, icon: Mail, label: t("sidebar.contact"),
      active: isActive(ROUTES.contacts), visible: isContracted("contact") && (canListContacts || canReviewSubmits),
      subItems: [
        { href: `${ROUTES.contacts}?tab=messages`, label: t("sidebar.sub.contactMessages"), active: tab === "messages" || !tab, visible: canListContacts },
        { href: `${ROUTES.contacts}?tab=submits`, label: t("sidebar.sub.accessRequests"), active: tab === "submits", visible: canReviewSubmits },
        { href: `${ROUTES.contacts}?tab=client_submits`, label: t("sidebar.sub.clientRegistration"), active: tab === "client_submits", visible: canReviewSubmits },
        { href: `${ROUTES.contacts}?tab=collaborator_submits`, label: t("sidebar.sub.collabRegistration"), active: tab === "collaborator_submits", visible: canReviewSubmits },
      ],
    },
    {
      key: "settings", href: ROUTES.settings, icon: Settings, label: t("sidebar.settings"),
      active: isActive(ROUTES.settings), visible: hasSettingsModuleAccess,
      subItems: [
        { href: `${ROUTES.settings}?tab=general`, label: t("settings.tabs.general"), active: tab === "general" || !tab, visible: canListSystemProfiles },
        { href: `${ROUTES.settings}?tab=security`, label: t("settings.tabs.security"), active: tab === "security", visible: canListSystemProfiles },
        { href: `${ROUTES.settings}?tab=website`, label: t("sidebar.sub.website"), active: tab === "website", visible: canListSystemProfiles && isContracted("website") },
        { href: `${ROUTES.settings}?tab=access`, label: t("sidebar.sub.accessPermissions"), active: tab === "access" || tab === "permissions", visible: canListRoles || canListPermission },
        { href: `${ROUTES.settings}?tab=predefinitions`, label: t("settings.tabs.predefinitions"), active: tab === "predefinitions", visible: canManagePredefinitionsFlag },
      ],
    },
    {
      key: "app-hub", href: ROUTES.adminAppHub, icon: LayoutGrid, label: t("sidebar.appHub"),
      active: isActive(ROUTES.adminAppHub), visible: canListAppBuilds,
    },
    {
      key: "errors", href: ROUTES.adminErrors, icon: AlertTriangle, label: t("sidebar.errors"),
      active: isActive(ROUTES.adminErrors), visible: canViewErrors,
    },
    buildPlatformNavItem({ t, isActive, isPlatformAdmin }),
  ];

  const profileTabs = buildProfileTabs(t, tab);

  const renderSubItems = (subItems: NavSubItem[]) => (
    <SidebarMenuSub>
      {subItems.map((sub) => (
        <SidebarMenuSubItem key={sub.href}>
          <SidebarMenuSubButton asChild isActive={sub.active}>
            <Link href={sub.href} onClick={closeMobile}>
              <span>{sub.label}</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ))}
    </SidebarMenuSub>
  );

  const userName = userProfile?.username || "Usuário";

  return (
    <>
      <Sidebar mobileTitle="Navegação principal" className={isTauri ? "top-9 h-[calc(100dvh-2.25rem)]" : undefined}>
        <SidebarHeader className="flex-row items-center justify-between gap-2 px-4 py-5 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:px-2">
          <TenantLogo
            variant="mark"
            width={expanded ? 120 : 32}
            height={expanded ? 35 : 32}
            className="object-contain"
            priority
          />
          {isMobile ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Fechar menu"
              onClick={closeMobile}
              className="text-white/70 hover:bg-white/10 hover:text-nav_accent dark:hover:bg-white/10"
            >
              <X size={22} />
            </Button>
          ) : (
            !isAutoCollapsed && (
              <SidebarTrigger
                aria-label={state === "expanded" ? "Recolher menu" : "Expandir menu"}
                className="h-8 w-8 bg-dark_green text-white/70 hover:bg-dark_green-700 dark:hover:bg-dark_green-700"
              >
                {state === "expanded" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </SidebarTrigger>
            )
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarMenu className="px-2">
            {items.filter((item) => item.visible).map((item) => {
              const Icon = item.icon;
              const subItems = item.subItems?.filter((sub) => sub.visible !== false) ?? [];
              const showChildren = expanded && (item.expandChildren ?? item.active);

              return (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton asChild isActive={item.active} tooltip={item.label}>
                    <Link href={item.href} onClick={closeMobile}>
                      <Icon />
                      <span className="flex-1">{item.label}</span>
                      {item.badge ? (
                        <span
                          aria-label={`${item.badge} por ler`}
                          className="min-w-[18px] h-[18px] px-1 rounded-full bg-nav_accent text-black text-[11px] font-semibold flex items-center justify-center group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:right-1 group-data-[collapsible=icon]:top-1 group-data-[collapsible=icon]:min-w-[8px] group-data-[collapsible=icon]:h-2 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:text-[0px]"
                        >
                          {item.badge > 9 ? "9+" : item.badge}
                        </span>
                      ) : null}
                    </Link>
                  </SidebarMenuButton>
                  {showChildren && subItems.length > 0 && renderSubItems(subItems)}
                  {showChildren && item.submenu}
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarContent>

        <SidebarSeparator />

        <SidebarFooter className="gap-3">
          <TenantSwitcher expanded={expanded} />
          <div className="flex items-center justify-between gap-1 group-data-[collapsible=icon]:flex-col">
            <UtilityButton label={`${t("header.search")} (⌘K / Ctrl+K)`} onClick={() => setIsSearchModalOpen(true)}>
              <Search size={18} />
            </UtilityButton>
            <UtilityButton label={t("sidebar.sub.notifications")} onClick={() => setIsNotificationsModalOpen(true)}>
              <Bell size={18} />
              {totalUnread > 0 && (
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full border border-night_input bg-nav_accent animate-pulse" />
              )}
            </UtilityButton>
            <UtilityButton label={isDark ? "Alternar para tema claro" : "Alternar para tema escuro"} onClick={toggleTheme}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </UtilityButton>
          </div>

          <SidebarMenu>
            {isActive(ROUTES.me) && expanded && renderSubItems(profileTabs)}
            <SidebarMenuItem className="flex items-center gap-1">
              <SidebarMenuButton asChild size="lg" isActive={isActive(ROUTES.me)} tooltip={userName} className="bg-black/20 hover:bg-black/30">
                <Link href={ROUTES.me} onClick={closeMobile}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10">
                      <UserIcon size={18} />
                    </div>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{userName}</span>
                    <span className="block truncate text-[10px] tracking-wide text-white/60">{userProfile?.email || ""}</span>
                  </span>
                </Link>
              </SidebarMenuButton>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Sair"
                title="Sair"
                onClick={() => signOut?.()}
                className="shrink-0 text-white/50 hover:bg-rose-400/10 hover:text-rose-400 group-data-[collapsible=icon]:hidden dark:hover:bg-rose-400/10"
              >
                <LogOut size={16} />
              </Button>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} />
      <NotificationPanel isOpen={isNotificationsModalOpen} onClose={() => setIsNotificationsModalOpen(false)} />
    </>
  );
}
